import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { IconDownload, IconRefresh } from '@arco-design/web-react/icon';
import { adminUpstreamCheckApi } from '@/api';
import type { PaginatedResult } from '@/types/api';
import type {
  UpstreamCheckFindingItem,
  UpstreamCheckFindingQuery,
  UpstreamCheckSampleDetail,
  UpstreamCheckSampleItem,
  UpstreamCheckSetting,
} from '@/types/upstreamCheck';

const SEVERITY_COLOR: Record<string, string> = {
  info: 'blue',
  warn: 'orange',
  error: 'red',
};

const CODE_OPTIONS = [
  'MISSING_FIELD',
  'EXTRA_FIELD',
  'TYPE_MISMATCH',
  'ENUM_AMBIGUITY',
  'ENVELOPE_DIFF',
  'PATH_UNDOCUMENTED',
];

export default function UpstreamCheckView() {
  const [setting, setSetting] = useState<UpstreamCheckSetting | null>(null);
  const [settingLoading, setSettingLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [findings, setFindings] = useState<PaginatedResult<UpstreamCheckFindingItem> | null>(null);
  const [samples, setSamples] = useState<PaginatedResult<UpstreamCheckSampleItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<UpstreamCheckFindingQuery>({ page: 1, pageSize: 20 });
  const [samplePage, setSamplePage] = useState(1);
  const [detail, setDetail] = useState<UpstreamCheckSampleDetail | null>(null);
  const [exporting, setExporting] = useState(false);

  const loadSetting = useCallback(() => {
    setSettingLoading(true);
    adminUpstreamCheckApi
      .getSetting()
      .then(setSetting)
      .catch(() => null)
      .finally(() => setSettingLoading(false));
  }, []);

  const loadFindings = useCallback(() => {
    setLoading(true);
    adminUpstreamCheckApi
      .listFindings(query)
      .then(setFindings)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [query]);

  const loadSamples = useCallback(() => {
    adminUpstreamCheckApi
      .listSamples(samplePage, 20)
      .then(setSamples)
      .catch(() => null);
  }, [samplePage]);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  const onToggle = async (checked: boolean) => {
    setSaving(true);
    try {
      const s = await adminUpstreamCheckApi.updateSetting(checked);
      setSetting(s);
      Message.success(checked ? '已开启检查上游' : '已关闭检查上游');
    } finally {
      setSaving(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await adminUpstreamCheckApi.downloadExport();
      Message.success('产物包已开始下载');
    } catch {
      Message.error('导出失败');
    } finally {
      setExporting(false);
    }
  };

  const onClear = () => {
    Modal.confirm({
      title: '确认清空？',
      content: '将删除全部 findings 与精选样本，此操作不可恢复。',
      okButtonProps: { status: 'danger' },
      onOk: async () => {
        const r = await adminUpstreamCheckApi.clearAll();
        Message.success(`已清空 findings=${r.findingsDeleted} samples=${r.samplesDeleted}`);
        loadFindings();
        loadSamples();
      },
    });
  };

  const openSample = async (id: string) => {
    const d = await adminUpstreamCheckApi.sampleDetail(id);
    setDetail(d);
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">检查上游</div>
          <div className="admin-page__subtitle">
            对照 PDF/Mock baseline，采样真实上游漂移并导出 DRIFT-PACK 供更新 Mock
          </div>
        </div>
        <Space>
          <Button icon={<IconRefresh />} onClick={() => { loadSetting(); loadFindings(); loadSamples(); }}>
            刷新
          </Button>
          <Button type="primary" icon={<IconDownload />} loading={exporting} onClick={onExport}>
            下载产物包
          </Button>
          <Button status="danger" onClick={onClear}>
            清空数据
          </Button>
        </Space>
      </div>

      {!setting?.available && (
        <Alert
          type="warning"
          style={{ marginBottom: 16 }}
          content={setting?.reason || '当前环境不可用检查上游（需 production 且非 Mock）'}
        />
      )}

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <Space align="center" size="large">
          <Typography.Text>检查上游</Typography.Text>
          <Switch
            checked={Boolean(setting?.enabled)}
            disabled={!setting?.available}
            loading={settingLoading || saving}
            onChange={onToggle}
          />
          <Typography.Text type="secondary">
            {setting?.enabled
              ? '已开启：真实上游 JSON 响应将对照 baseline 并采样'
              : '已关闭：不产生新的 finding / 样本'}
          </Typography.Text>
        </Space>
      </div>

      <div className="admin-card" style={{ marginBottom: 16 }}>
        <Typography.Title heading={6} style={{ marginTop: 0 }}>
          Findings
        </Typography.Title>
        <Space style={{ marginBottom: 12 }} wrap>
          <Input
            allowClear
            placeholder="apiPath 过滤"
            style={{ width: 220 }}
            onChange={(v) => setQuery((q) => ({ ...q, apiPath: v || undefined, page: 1 }))}
          />
          <Select
            allowClear
            placeholder="code"
            style={{ width: 200 }}
            options={CODE_OPTIONS.map((c) => ({ value: c, label: c }))}
            onChange={(v) => setQuery((q) => ({ ...q, code: (v as string) || undefined, page: 1 }))}
          />
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          data={findings?.items || []}
          pagination={{
            current: query.page || 1,
            pageSize: query.pageSize || 20,
            total: findings?.total || 0,
            onChange: (page) => setQuery((q) => ({ ...q, page })),
          }}
          columns={[
            { title: 'Path', dataIndex: 'apiPath', width: 200 },
            {
              title: 'Code',
              dataIndex: 'code',
              width: 140,
              render: (v: string) => <Tag>{v}</Tag>,
            },
            {
              title: 'Severity',
              dataIndex: 'severity',
              width: 90,
              render: (v: string) => <Tag color={SEVERITY_COLOR[v] || 'gray'}>{v}</Tag>,
            },
            { title: 'Field', dataIndex: 'fieldPath', width: 160 },
            { title: 'Hits', dataIndex: 'hitCount', width: 70 },
            { title: 'Message', dataIndex: 'message', ellipsis: true },
            {
              title: 'Last seen',
              dataIndex: 'lastSeenAt',
              width: 170,
              render: (v: string) => new Date(v).toLocaleString(),
            },
          ]}
        />
      </div>

      <div className="admin-card">
        <Typography.Title heading={6} style={{ marginTop: 0 }}>
          精选样本
        </Typography.Title>
        <Table
          rowKey="id"
          data={samples?.items || []}
          pagination={{
            current: samplePage,
            pageSize: 20,
            total: samples?.total || 0,
            onChange: setSamplePage,
          }}
          columns={[
            { title: 'Path', dataIndex: 'apiPath' },
            { title: 'Why', dataIndex: 'whySelected' },
            {
              title: 'Non-extra',
              dataIndex: 'hasNonExtraFinding',
              width: 100,
              render: (v: boolean) => (v ? <Tag color="orange">yes</Tag> : <Tag>no</Tag>),
            },
            { title: 'Findings', dataIndex: 'findingCount', width: 90 },
            {
              title: 'Created',
              dataIndex: 'createdAt',
              width: 170,
              render: (v: string) => new Date(v).toLocaleString(),
            },
            {
              title: '操作',
              width: 90,
              render: (_, row) => (
                <Button type="text" size="small" onClick={() => openSample(row.id)}>
                  详情
                </Button>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        width={560}
        title="样本详情"
        visible={Boolean(detail)}
        onCancel={() => setDetail(null)}
        footer={null}
      >
        {detail && (
          <Space direction="vertical" style={{ width: '100%' }} size="medium">
            <Typography.Text>
              {detail.apiPath} · {detail.whySelected}
            </Typography.Text>
            <Typography.Text bold>Findings</Typography.Text>
            <pre className="admin-mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(detail.findings, null, 2)}
            </pre>
            <Typography.Text bold>Request (redacted)</Typography.Text>
            <pre className="admin-mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(detail.requestParams, null, 2)}
            </pre>
            <Typography.Text bold>Response (redacted)</Typography.Text>
            <pre className="admin-mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(detail.responsePayload, null, 2)}
            </pre>
          </Space>
        )}
      </Drawer>
    </div>
  );
}
